import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShoppingBag, Sparkles, Users, Heart, ShieldCheck, Globe, BadgeCheck, Receipt } from 'lucide-react';

const HowNanakiWorks = () => {
  return (
    <div className="bg-[#F9F8F6] text-stone-800" data-testid="how-nanaki-works-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#FBF7F2] via-[#F4ECE2] to-[#F9F8F6]">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
          backgroundImage: 'radial-gradient(#4A3043 1px, transparent 1px)',
          backgroundSize: '24px 24px'
        }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center">
          <span className="inline-block text-xs uppercase tracking-[0.3em] text-stone-500 mb-6">
            How Nanaki Works
          </span>
          <h1 className="font-serif text-5xl md:text-7xl leading-tight text-[#2A1B2D] mb-8" style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}>
            A women-led ecosystem<br className="hidden md:block" />{' '}
            for <em className="text-[#B6594C]">transparent</em>, accountable,<br className="hidden md:block" />{' '}
            community-driven trade.
          </h1>
          <p className="text-lg md:text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed">
            Nanaki connects brands, shoppers, and influencers through verified order coordination —
            where every transaction creates income, exposure, and impact across the entire community.
          </p>
        </div>
      </section>

      {/* OVERVIEW BANNER */}
      <section className="py-14 bg-white border-y border-[#E8DCC8]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-stone-700 leading-relaxed text-lg">
            <strong>Nanaki does not directly process online payments on its platform.</strong> Instead, it
            facilitates end-to-end order coordination using each brand's own verified payment
            link, ensuring authenticity and trust for every transaction.
          </p>
        </div>
      </section>

      {/* SUPPLY SIDE */}
      <section className="py-20 md:py-28 bg-[#FBF7F2]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#B6594C] mb-4">
                On the Supply Side
              </span>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight text-[#2A1B2D] mb-6" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                A modern distribution &amp; retail partner for brands.
              </h2>
              <p className="text-stone-600 leading-relaxed mb-6">
                Nanaki helps partner brands expand their reach through influencer-led discovery,
                community-driven sales, customer engagement, verified order coordination, and
                transparent purchase confirmation systems.
              </p>
              <p className="text-stone-600 leading-relaxed">
                Once a shopper places an order, our team coordinates the communication flow and
                sends invoice confirmations to the <strong>shopper</strong>, the <strong>brand</strong>,
                and the <strong>influencer</strong> — keeping every stakeholder informed and accountable.
              </p>
            </div>

            <div className="bg-white border border-[#E8DCC8] rounded-2xl p-8">
              <div className="w-12 h-12 rounded-full bg-[#B6594C]/10 text-[#B6594C] flex items-center justify-center mb-6">
                <Sparkles className="w-6 h-6" />
              </div>
              <h3 className="font-serif text-2xl mb-4 text-[#2A1B2D]" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                What brands gain
              </h3>
              <ul className="space-y-3 text-stone-600">
                {[
                  'Incremental sales opportunities',
                  'Access to a conscious consumer base',
                  'A scalable affiliate ecosystem',
                  'Stronger community visibility',
                  'Association with women-empowerment initiatives'
                ].map((it) => (
                  <li key={it} className="flex items-start">
                    <span className="text-[#B6594C] mr-3 mt-1">◆</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* DEMAND SIDE */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div className="bg-[#4A3043] text-white rounded-2xl p-8 order-2 md:order-1">
              <div className="w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center mb-6">
                <Users className="w-6 h-6" />
              </div>
              <h3 className="font-serif text-2xl mb-4" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                For affiliates &amp; creators
              </h3>
              <p className="text-white/80 mb-4 leading-relaxed">
                Influencers, creators, homemakers, students, artists, professionals, and community
                leaders earn transparent commissions on successful sales — no inventory, warehousing,
                or operational infrastructure needed.
              </p>
              <ul className="space-y-3 text-white/90">
                {[
                  'Verified — every transaction is checked',
                  'Accounted for — recorded on a transparent ledger',
                  'Tracked — visible in your influencer dashboard',
                  'Reported — clearly to all stakeholders'
                ].map((it) => (
                  <li key={it} className="flex items-start">
                    <span className="text-[#E8DCC8] mr-3 mt-1">◆</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="order-1 md:order-2">
              <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#B6594C] mb-4">
                On the Demand Side
              </span>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight text-[#2A1B2D] mb-6" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                An affiliate marketing ecosystem for women, from home.
              </h2>
              <p className="text-stone-600 leading-relaxed mb-6">
                Nanaki enables women from all walks of life to earn a livelihood from their homes.
                Through the platform, anyone can promote products from aligned brands and earn
                ethical commissions on every successful sale.
              </p>
              <p className="text-stone-600 leading-relaxed">
                This creates a trustworthy ecosystem where women can <strong>participate in
                commerce</strong> without the friction of running their own operations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* THE ORDER FLOW (visual) */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-[#F4ECE2] to-[#FBF7F2]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#B6594C] mb-4">
              How an order flows
            </span>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight text-[#2A1B2D]" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
              From shop to shipment to invoice.
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { i: <ShoppingBag className="w-6 h-6" />, t: 'Shopper places order', d: 'Customer adds items to cart, enters address and (optional) influencer code, and confirms.' },
              { i: <ShieldCheck className="w-6 h-6" />, t: 'Nanaki coordinates', d: 'Our team reaches out to collect payment offline via the brand\'s verified payment link.' },
              { i: <Globe className="w-6 h-6" />, t: 'Order delivered', d: 'Nanaki coordinates dispatch and delivery to the shopper\'s saved address.' },
              { i: <Receipt className="w-6 h-6" />, t: 'Invoices to all', d: 'A unified invoice is shared with the shopper, the brand, and the influencer who referred the sale.' }
            ].map((s, i) => (
              <div key={i}>
                <div className="w-12 h-12 rounded-full bg-[#B6594C]/10 text-[#B6594C] flex items-center justify-center mb-4">{s.i}</div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#B6594C]">Step {i + 1}</span>
                <h3 className="font-bold text-lg mt-1 mb-2 text-[#2A1B2D]">{s.t}</h3>
                <p className="text-stone-600 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNITY BENEFITS */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#B6594C] mb-4">
              Benefits for the Community
            </span>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight text-[#2A1B2D]" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
              Every purchase, a contribution.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { i: <BadgeCheck className="w-5 h-5" />, t: 'Shoppers', d: 'Receive exclusive benefits and become part of a purpose-driven ecosystem.' },
              { i: <Sparkles className="w-5 h-5" />, t: 'Brands', d: 'Gain authentic reach through trusted community voices.' },
              { i: <Users className="w-5 h-5" />, t: 'Influencers', d: 'Earn through ethical, transparent affiliate marketing.' },
              { i: <Heart className="w-5 h-5" />, t: 'Communities', d: 'Women-led collectives gain real opportunities for financial participation and independence.' }
            ].map((b) => (
              <div key={b.t} className="bg-[#FBF7F2] border border-[#E8DCC8] rounded-2xl p-6">
                <div className="w-10 h-10 rounded-lg bg-[#B6594C]/10 text-[#B6594C] flex items-center justify-center mb-4">{b.i}</div>
                <h3 className="font-bold mb-2 text-[#2A1B2D]">{b.t}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="py-20 md:py-28 bg-[#2A1B2D] text-white text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-serif text-2xl md:text-3xl leading-relaxed mb-8" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
            Nanaki believes commerce can become a tool for <em className="text-[#E8DCC8]">empowerment, transparency, and community building</em> — where every purchase contributes towards a larger social impact.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-12">
            <Link
              to="/products"
              className="inline-flex items-center justify-center bg-[#B6594C] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#9d4a40] transition"
              data-testid="hnw-cta-shop"
            >
              Shop the marketplace
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <Link
              to="/register-angel"
              className="inline-flex items-center justify-center border border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition"
              data-testid="hnw-cta-angel"
            >
              Become an influencer
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowNanakiWorks;
